import { PencilIcon, XIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from '@rocco/components/drawer';
import { Separator } from '@rocco/components/separator';
import type { MeUser } from '~/server/types';
import type { PartialConversation } from '../../types';
import { DRAWER_WIDTH_REM, useChat } from '../../hooks/use-chat';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';
import { ChatInboxFloatingButton } from './ChatInboxFloatingButton';
import { ChatInboxList } from './ChatInboxList';

/**
 * The floating chat inbox bubble and the chat inbox drawer.
 */
export function ChatInboxDrawer({
    children,
    className,
    conversations,
    user,
}: {
    children?: React.ReactNode;
    className?: string;
    /**
     * The user's list of conversations. A `null` value means that the conversations
     * are not yet fetched.
     */
    conversations: PartialConversation[] | null;
    /**
     * The logged-in user.
     */
    user: MeUser;
}) {
    const { chatOpenState, setChatOpenState, openChatBox } = useChat();

    return (
        <Drawer
            open={chatOpenState === 'open'}
            direction="right"
            modal={false}
            dismissible={false}
            repositionInputs={false}
            onOpenChange={(open) => setChatOpenState(open ? 'open' : 'closed')}
        >
            <DrawerTrigger asChild>
                <ChatInboxFloatingButton
                    className={cn(
                        // Prevent the floating button from shifting when the scrollbar is locked.
                        '[body[data-scroll-locked="1"]_&]:translate-x-[calc(0px-var(--removed-body-scroll-bar-size,14px))]',
                        className,
                        chatOpenState === 'open' && 'hidden'
                    )}
                />
            </DrawerTrigger>
            {/* Make the `DrawerContent` take up the whole screen, render the drawer
            on the right and the children (chat boxes) to the left of the drawer. */}
            <DrawerContent
                className={cn(
                    // Make the `DrawerContent` full screen.
                    'data-[vaul-drawer-direction=right]:inset-0 data-[vaul-drawer-direction=right]:w-unset data-[vaul-drawer-direction=right]:sm:max-w-unset',
                    // Disable the default Drawer slide-in animation, implement custom animation.
                    'animate-none transition-none',
                    // Prevent the drawer content from shifting when the scrollbar is locked.
                    '[body[data-scroll-locked="1"]_&]:translate-x-[calc(0px-var(--removed-body-scroll-bar-size,14px))]',
                    'bg-transparent'
                )}
                noHandle
                hideDefaultCloseButton
            >
                <div className="relative flex h-full w-full items-end overflow-hidden">
                    {/* Fake overlay to close drawer on click */}
                    <div
                        className="absolute inset-0 bg-black/20 duration-500 animate-in fade-in"
                        onClick={() => setChatOpenState('closed')}
                    ></div>
                    {/* Need to place drawer content and children z-index >= 0 to
                    prevent clicking the overlay */}
                    {children}
                    <div
                        className="ml-auto h-full shrink-0 py-4 pr-4"
                        style={{ width: `${DRAWER_WIDTH_REM}rem` }}
                    >
                        <div className="z-1 flex size-full flex-col rounded-xl bg-white/60 text-black backdrop-blur-xl duration-500 animate-in slide-in-from-bottom">
                            {/* Suppress missing title and description console warning */}
                            <DrawerHeader className="hidden" aria-hidden>
                                <DrawerTitle className="hidden" aria-hidden>
                                    Messages
                                </DrawerTitle>
                                <DrawerDescription
                                    className="hidden"
                                    aria-hidden
                                />
                            </DrawerHeader>
                            {/* Header */}
                            <div className="relative flex select-text items-center gap-3 py-8 pl-4 pr-16">
                                <ParticipantAvatar
                                    size="square56"
                                    participant={user}
                                />
                                <div className="min-w-0 flex-1 space-y-1 leading-tight">
                                    <div className="text-4.5 font-medium">
                                        {user.Name}
                                    </div>
                                    <div className="truncate text-3.5 opacity-70">
                                        {user.Email}
                                    </div>
                                </div>
                                <button
                                    className="absolute right-6 top-6 p-1"
                                    aria-label="Close"
                                    onClick={() => setChatOpenState('closed')}
                                >
                                    <XIcon className="size-6 opacity-70 hover:opacity-100" />
                                </button>
                            </div>
                            {/* Inbox list */}
                            <ChatInboxList
                                className="min-h-0 flex-1 px-4 py-2"
                                conversations={conversations ?? []}
                                isLoading={!conversations}
                            />
                            <div className="px-4">
                                <Separator
                                    className="bg-white"
                                    orientation="horizontal"
                                />
                            </div>
                            {/* New message button */}
                            <div className="flex items-center justify-center px-2 py-8">
                                <Button
                                    className="flex items-center gap-2"
                                    color="dark"
                                    size="larger"
                                    onClick={() => openChatBox(0)}
                                >
                                    New Message
                                    <PencilIcon className="size-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    );
}
